import type { QuizConfig, QuizStep, QuizStepType } from "../../types"
import { type StepDescriptor, isFilled, matchesPattern } from "./step-contract"
import { SingleChoiceStep } from "./single-choice-step"
import { MultipleChoiceStep } from "./multiple-choice-step"
import { InputStep } from "./input-step"
import { CityCombobox } from "../city-combobox"
import { EmailStep } from "./email-step"
import { DateStep } from "./date-step"
import { TimeStep } from "./time-step"
import { AnalysisStep } from "./analysis-step"
import { InfoStep } from "./info-step"
import { PhotoStep } from "./photo-step"
import { CoverStep } from "./cover-step"

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const required =
  (validate?: StepDescriptor["validate"]): StepDescriptor["validate"] =>
  (value, step) =>
    !step.required ? true : (validate ?? isFilled)(value, step)

/**
 * Maps a step type to its component and behaviour. Adding a new step type =
 * one entry here; the `Record<QuizStepType, ...>` makes coverage exhaustive.
 */
export const stepRegistry: Record<QuizStepType, StepDescriptor> = {
  "single-choice": {
    autoAdvance: true,
    validate: required(),
    Component: ({ step, value, onChange }) => (
      <SingleChoiceStep
        step={step}
        value={typeof value === "string" ? value : undefined}
        onChange={onChange}
      />
    ),
  },
  "multiple-choice": {
    validate: required(),
    Component: ({ step, value, onChange }) => (
      <MultipleChoiceStep
        step={step}
        value={Array.isArray(value) ? value : []}
        onChange={onChange}
      />
    ),
  },
  input: {
    validate: required(matchesPattern),
    Component: ({ step, value, onChange }) => (
      <InputStep step={step} value={typeof value === "string" ? value : ""} onChange={onChange} />
    ),
  },
  "input-city": {
    validate: required(),
    Component: ({ step, value, onChange }) => (
      <CityCombobox
        value={typeof value === "string" ? value : ""}
        onValueChange={onChange}
        placeholder={step.placeholder}
      />
    ),
  },
  email: {
    validate: required((value) => typeof value === "string" && EMAIL_RE.test(value)),
    Component: ({ step, value, onChange }) => (
      <EmailStep step={step} value={typeof value === "string" ? value : ""} onChange={onChange} />
    ),
  },
  date: {
    validate: required(),
    Component: DateStep,
  },
  time: {
    validate: required(),
    Component: ({ value, onChange }) => (
      <div className="flex flex-1 items-center">
        <TimeStep value={typeof value === "string" ? value : ""} onChange={onChange} />
      </div>
    ),
  },
  analysis: {
    fullBleed: true,
    validate: () => true,
    Component: ({ step, answers, onAdvance }) => (
      <AnalysisStep
        title={step.title}
        checklist={step.checklist ?? []}
        birthDate={typeof answers.dob === "string" ? answers.dob : undefined}
        onDone={onAdvance}
      />
    ),
  },
  info: {
    validate: () => true,
    Component: ({ step }) => <InfoStep step={step} />,
  },
  photo: {
    validate: required(),
    Component: PhotoStep,
  },
  cover: {
    fullBleed: true,
    validate: () => true,
    Component: CoverStep,
  },
}

/**
 * The descriptor a quiz should use for a step type: its own override if it
 * declares one, otherwise the shared default. Single source of truth for the
 * renderer and `use-quiz`.
 */
export function resolveDescriptor(config: QuizConfig, type: QuizStepType): StepDescriptor {
  return config.stepOverrides?.[type] ?? stepRegistry[type]
}

/**
 * The descriptor for a concrete step: a one-off `step.component` (full-bleed,
 * owns its CTA so it never gates on the shared Continue) wins; otherwise fall
 * back to the per-type resolution above.
 */
export function resolveStepDescriptor(config: QuizConfig, step: QuizStep): StepDescriptor {
  if (step.component) {
    return { Component: step.component, fullBleed: step.fullBleed ?? true, validate: () => true }
  }
  return resolveDescriptor(config, step.type)
}
